import {
  useGeneralSettingsFormData,
  useUpdateGeneralSettings,
} from "@/api/cms.ts";
import { CmsAdvancedModeToggle } from "@/components/cms/CmsAdvancedModeToggle";
import { GeneralSettingsForm } from "@/components/cms/GeneralSettingsForm";
import { FormFieldVisibilityProvider } from "@/context/FormFieldVisibilityContext";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { generalSettingsSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type CmsGeneralSettingsForm = z.infer<typeof generalSettingsSchema>;

/**
 * Éditeur des réglages généraux (header sticky, réseaux sociaux, langues,
 * lecteur vidéo, mode CR mixte + bascule CMS avancé) — EXTRAIT de la page pour
 * être réutilisé par l'interface WordPress-like du CMS avancé
 * (`/cms/advanced/reglages`). Rend le `<form id="general-settings">` : l'hôte
 * (CMSLayout ou CmsAdminShell) fournit le bouton Enregistrer qui
 * `requestSubmit()` ce formulaire.
 */
export const GeneralSettingsEditor = () => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: generalSettingsData } = useGeneralSettingsFormData();
  const updateGeneralSettings = useUpdateGeneralSettings();

  const defaultValues: CmsGeneralSettingsForm = useMemo(
    () => ({
      headerAlwaysVisible: generalSettingsData?.headerAlwaysVisible ?? false,
      headerAlwaysVisibleMobile:
        generalSettingsData?.headerAlwaysVisibleMobile ?? false,
      socials: generalSettingsData?.socials ?? [],
      isMixedCRMode: generalSettingsData?.isMixedCRMode ?? false,
      defaultWebsiteLanguage:
        generalSettingsData?.defaultWebsiteLanguage ?? "fr",
      websiteLanguages: generalSettingsData?.websiteLanguages ?? [
        "fr",
        "en",
        "es",
        "nl",
      ],
      playerAutoplay: generalSettingsData?.playerAutoplay ?? false,
      playerControls: generalSettingsData?.playerControls ?? true,
      playerMuted: generalSettingsData?.playerMuted ?? false,
      playerLoop: generalSettingsData?.playerLoop ?? false,
      maintenanceEnabled: generalSettingsData?.maintenanceEnabled ?? false,
      maintenancePageId: generalSettingsData?.maintenancePageId ?? null,
      abonnementPageId: generalSettingsData?.abonnementPageId ?? null,
    }),
    [generalSettingsData],
  );

  const form = useForm<CmsGeneralSettingsForm>({
    resolver: zodResolver(generalSettingsSchema),
    defaultValues,
  });

  const { reset } = form;
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (
      !hasInitialized.current &&
      generalSettingsData != null &&
      Object.keys(defaultValues).length > 0
    ) {
      reset(defaultValues);
      hasInitialized.current = true;
    }
  }, [defaultValues, generalSettingsData, reset]);

  const onSubmit = (submittedData: CmsGeneralSettingsForm) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    const defaultLang = submittedData.defaultWebsiteLanguage;
    const websiteLanguages = [
      ...new Set([defaultLang, ...submittedData.websiteLanguages]),
    ];

    updateGeneralSettings.mutate(
      {
        id: instanceData.CMS.id,
        headerAlwaysVisible: submittedData.headerAlwaysVisible,
        headerAlwaysVisibleMobile: submittedData.headerAlwaysVisibleMobile,
        socials: submittedData.socials,
        isMixedCRMode: submittedData.isMixedCRMode,
        defaultWebsiteLanguage: defaultLang,
        websiteLanguages,
        playerAutoplay: submittedData.playerAutoplay,
        playerControls: submittedData.playerControls,
        playerMuted: submittedData.playerMuted,
        playerLoop: submittedData.playerLoop,
        maintenanceEnabled: submittedData.maintenanceEnabled,
        maintenancePageId: submittedData.maintenancePageId ?? null,
        abonnementPageId: submittedData.abonnementPageId ?? null,
      },
      {
        onSuccess: async () => {
          await queryClient.refetchQueries({
            queryKey: ["cms", "general-settings"],
          });

          const freshGeneralSettings =
            queryClient.getQueryData<CmsGeneralSettingsForm>([
              "cms",
              "general-settings",
            ]);

          reset({
            headerAlwaysVisible:
              freshGeneralSettings?.headerAlwaysVisible ??
              submittedData.headerAlwaysVisible,
            headerAlwaysVisibleMobile:
              freshGeneralSettings?.headerAlwaysVisibleMobile ??
              submittedData.headerAlwaysVisibleMobile,
            socials: freshGeneralSettings?.socials ?? submittedData.socials,
            isMixedCRMode:
              freshGeneralSettings?.isMixedCRMode ??
              submittedData.isMixedCRMode,
            defaultWebsiteLanguage:
              freshGeneralSettings?.defaultWebsiteLanguage ?? defaultLang,
            websiteLanguages:
              freshGeneralSettings?.websiteLanguages ?? websiteLanguages,
            playerAutoplay:
              freshGeneralSettings?.playerAutoplay ??
              submittedData.playerAutoplay,
            playerControls:
              freshGeneralSettings?.playerControls ??
              submittedData.playerControls,
            playerMuted:
              freshGeneralSettings?.playerMuted ?? submittedData.playerMuted,
            playerLoop:
              freshGeneralSettings?.playerLoop ?? submittedData.playerLoop,
            maintenanceEnabled:
              freshGeneralSettings?.maintenanceEnabled ??
              submittedData.maintenanceEnabled,
            maintenancePageId:
              freshGeneralSettings?.maintenancePageId ??
              submittedData.maintenancePageId ??
              null,
            abonnementPageId:
              freshGeneralSettings?.abonnementPageId ??
              submittedData.abonnementPageId ??
              null,
          });

          toast({
            title: t("success"),
            description: t("homepageSuccessMessage"),
            duration: 4000,
            variant: "success",
          });
        },
        onError: (error: unknown) => {
          const apiError = error as {
            response?: { data?: { message?: string | string[] } };
          };
          const message = apiError.response?.data?.message;
          const description = Array.isArray(message)
            ? message.join(", ")
            : typeof message === "string"
              ? message
              : t("homepageErrorMessage");

          toast({
            title: t("error"),
            description,
            variant: "error",
            duration: 5000,
          });
        },
      },
    );
  };

  return (
    <div className="w-full">
      <FormProvider {...form}>
        <FormFieldVisibilityProvider formKey="cms-general-settings">
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            id="general-settings"
            className="w-full"
          >
            <GeneralSettingsForm />
          </form>
        </FormFieldVisibilityProvider>
      </FormProvider>

      <div className="mt-8 w-full">
        <CmsAdvancedModeToggle />
      </div>
    </div>
  );
};

/** Page classique (`/cms/general-settings`) : l'éditeur dans le layout de l'ancien CMS. */
const CMSGeneralSettings = () => {
  const { t } = useTranslation();
  return (
    <CMSLayout
      pageName={t("cms.generalSettings.title")}
      formIds={["general-settings"]}
      hidePageAppearanceButton
    >
      <GeneralSettingsEditor />
    </CMSLayout>
  );
};

export default CMSGeneralSettings;
